// La ficha del modo Redes para el Agente — ADR-2026-10-01-modo-redes, con el patrón de
// `describirAnimar` (ADR-2026-09-10-el-agente-toca-la-pantalla): la pantalla registra un
// controlador que se DESCRIBE (qué hay puesto en cada campo) y APLICA cambios validados
// con sus propios setters. Es lo que vuelve útil a "Ayuda de prompt": el agente del panel
// lateral lee el prompt que el usuario tiene a medias, lo mejora y lo deja escrito en la
// caja; el usuario toca Generar.
//
// Puro: sin React. Los ids de campo son los de `AYUDA_DE_CAMPOS` (así la skill de modos los
// nombra con el resto) salvo el prompt, que acá se llama "Prompt" aunque viaje con el id
// `instruccion`: el id es la clave estable del agente; la etiqueta es lo que ve el usuario.

import type { CambioValidado, CampoDePantalla, DescripcionDePantalla } from '../../../../chat-atlas';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { OPCIONES_RESOLUCION, type ResolucionImagen } from '../../../../lib/resolucionImagen';
import { AYUDA_DE_CAMPOS, QUE_HACE_EL_MODO, TOPES } from '../../puente/camposDeModo';
import { MAX_CANTIDAD, VIDEO_DURATION_MIN, videoDuracionMax } from '../asistente/directMode';
import { creditosDeRedes, faltaEnRedes, formatoActivo, type AjustesDeRedes } from './redes';

export const CLAVE_PANTALLA_REDES = 'modo:redes';

export interface DatosDeRedes {
  ruta: string;
  prompt: string;
  ajustes: AjustesDeRedes;
  cantidadAdjuntos: number;
  /** Nombres de lo adjunto, para que el agente sepa con qué cuenta ("Producto: Campera"). */
  adjuntos: string[];
  /** Cómo se menciona cada adjunto en el prompt ("@Campera", "@Imagen 1"), en su orden. */
  menciones?: string[];
  /** Si ya hay una sesión con lienzo o se está en la portada. */
  enSesion: boolean;
}

const base = (id: keyof typeof AYUDA_DE_CAMPOS) => ({
  id,
  etiqueta: AYUDA_DE_CAMPOS[id].etiqueta,
  queEs: AYUDA_DE_CAMPOS[id].queEs,
});

export function describirRedes(d: DatosDeRedes): DescripcionDePantalla {
  const a = d.ajustes;
  const esVideo = a.medio === 'video';
  const formato = formatoActivo(a);
  const formatoActual = FORMAT_OPTIONS_V2.find((f) => f.id === formato);
  const campos: CampoDePantalla[] = [
    {
      id: 'instruccion',
      etiqueta: 'Prompt',
      queEs: esVideo
        ? 'Qué video querés: qué pasa, con qué producto, en qué clima. Una IA lo convierte en el prompt del video.'
        : 'Qué imagen querés: la escena, el producto, la luz, el estilo. Es lo único obligatorio. Una IA lo redacta con tus mismas reglas de imagen y elige el motor antes de generar.',
      tipo: 'texto',
      valor: d.prompt,
      valorLegible: d.prompt ? `"${d.prompt.length > 100 ? `${d.prompt.slice(0, 97)}…` : d.prompt}"` : '(vacío)',
      max: TOPES.instruccion,
      editable: true,
    },
    {
      ...base('formato'),
      tipo: 'opcion',
      valor: formato,
      valorLegible: formatoActual?.name ?? formato,
      opciones: FORMAT_OPTIONS_V2.map((f) => ({ id: f.id, etiqueta: f.name })),
      editable: true,
    },
  ];
  if (esVideo) {
    const max = videoDuracionMax(a.video.ajustes.quality);
    campos.push({
      ...base('duracion'),
      tipo: 'numero',
      valor: a.video.ajustes.durationSeconds,
      valorLegible: `${a.video.ajustes.durationSeconds} segundos`,
      min: VIDEO_DURATION_MIN,
      max,
      entero: true,
      editable: true,
      nota: `Calidad ${a.video.ajustes.quality === 'profesional' ? 'Profesional (hasta 30 s)' : 'Básica (hasta 15 s)'}, ${a.video.ajustes.resolution}; se cambian en pantalla.`,
    });
  } else {
    const actual = OPCIONES_RESOLUCION.find((o) => o.id === a.imagen.calidad);
    campos.push(
      {
        ...base('resolucion'),
        tipo: 'opcion',
        valor: a.imagen.calidad,
        valorLegible: actual?.label ?? a.imagen.calidad,
        opciones: OPCIONES_RESOLUCION.map((o, i) => ({
          id: o.id,
          etiqueta: o.label,
          nota: `${i + 1} ${i === 0 ? 'crédito' : 'créditos'} por imagen`,
        })),
        editable: true,
      },
      {
        ...base('cantidad'),
        tipo: 'numero',
        valor: a.imagen.cantidad,
        valorLegible: String(a.imagen.cantidad),
        min: 1,
        max: MAX_CANTIDAD,
        entero: true,
        editable: true,
      },
    );
  }
  const falta = faltaEnRedes({ ajustes: a, prompt: d.prompt, listo: true, cantidadAdjuntos: d.cantidadAdjuntos });
  const creditos = creditosDeRedes(a);
  return {
    pantalla: 'el modo Redes',
    ruta: d.ruta,
    queHace: QUE_HACE_EL_MODO.redes,
    paso: `${esVideo ? 'video' : 'imagen'}${d.enSesion ? ', en una sesión con lienzo' : ', en la portada'}`,
    campos,
    faltaParaGenerar: falta ? [falta] : [],
    costo: `${creditos} ${creditos === 1 ? 'crédito' : 'créditos'}`,
    notas: [
      `El interruptor está en ${esVideo ? 'Video' : 'Imagen'}; se cambia en pantalla, no desde acá.`,
      d.adjuntos.length > 0 ? `Adjuntos: ${d.adjuntos.join(', ')}.` : 'Sin producto, modelo ni imágenes adjuntas.',
      'Generar lo hace el usuario con el botón; lo generado cae en el lienzo de la sesión.',
      ...(d.menciones && d.menciones.length > 0
        ? [
            `Para nombrar un adjunto en el prompt escribí @ y su nombre: ${d.menciones.join(', ')}. Nunca @Image1 ni otro número: la pantalla traduce el nombre a la posición al generar.`,
          ]
        : []),
      ...(esVideo ? ['Generar video requiere plan Pro o Ultra.'] : []),
    ],
  };
}

export interface AccionesDeRedes {
  setPrompt: (v: string) => void;
  setFormato: (slug: string) => void;
  setCalidadImagen: (v: ResolucionImagen) => void;
  setCantidad: (n: number) => void;
  setDuracion: (s: number) => void;
}

/** Aplica los cambios ya validados por el puente con los MISMOS setters que usan las pastillas. */
export function aplicarEnRedes(cambios: CambioValidado[], acciones: AccionesDeRedes): { aplicados: string[]; fallidos: { campo: string; motivo: string }[] } {
  const aplicados: string[] = [];
  const fallidos: { campo: string; motivo: string }[] = [];
  for (const c of cambios) {
    switch (c.campo) {
      case 'instruccion':
        acciones.setPrompt(String(c.valor));
        break;
      case 'formato':
        acciones.setFormato(String(c.valor));
        break;
      case 'resolucion':
        acciones.setCalidadImagen(String(c.valor) as ResolucionImagen);
        break;
      case 'cantidad':
        acciones.setCantidad(Number(c.valor));
        break;
      case 'duracion':
        acciones.setDuracion(Number(c.valor));
        break;
      default:
        fallidos.push({ campo: c.campo, motivo: 'ese campo no está en esta pantalla' });
        continue;
    }
    aplicados.push(c.campo);
  }
  return { aplicados, fallidos };
}
